import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { ErrorList, InputLabelWrapper } from '@guildeducationinc/recess';
import SearchIcon from '@guildeducationinc/recess/icons/SearchIcon';
import { Autocomplete, InputAdornment, TextField } from '@mui/material';
import { uniqueId } from 'lodash';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Currency } from '../../types';
/**
 * CurrencySelect component allows users to select a currency from a searchable dropdown list.
 *
 * @component
 * @param {CurrencySelectProps} props - The component props.
 * @param {string} props.className - The CSS class name for the component.
 * @param {string} props.label - The label for the select component.
 * @param {string} props.value - The currently selected currency code.
 * @param {string[]} props.errors - An array of error messages.
 * @param {Function} props.onChange - The callback function to handle currency selection changes.
 * @example
 * // Example usage of CurrencySelect component
 * <CurrencySelect
 *   className="currency-select"
 *   label="Select Currency"
 *   value="USD"
 *   errors={[]}
 *   onChange={(value) => console.log(value)}
 * />
 */
export const CurrencySelect = ({ className, label, value, errors, onChange, }) => {
    const intl = useIntl();
    const inputId = uniqueId('currency-select-');
    if (!label)
        label = intl.formatMessage({ id: 'Currency' });
    /**
     * Generates an array of currency options from the Currency enum.
     * Format: "CODE - Name" (e.g., "USD - US Dollar")
     */
    const options = useMemo(() => {
        // Convert Currency enum to array of options
        const currencies = Object.entries(Currency).map(([code, name]) => ({
            code,
            name,
            value: code,
            label: `${code} - ${name}`,
        }));
        // Sort alphabetically by code
        return currencies.sort((a, b) => a.code.localeCompare(b.code));
    }, []);
    /**
     * Handles the currency selection change event.
     */
    const handleChange = (_, selection) => {
        const currencyCode = selection?.code || '';
        onChange(currencyCode);
    };
    const selectedOption = options.find(opt => opt.code === value) || null;
    return (_jsxs("div", { className: className, children: [_jsx(InputLabelWrapper, { label: label, inputId: inputId }), _jsx(Autocomplete, { id: inputId, value: selectedOption, onChange: handleChange, options: options, getOptionLabel: option => option.label || option.code, isOptionEqualToValue: (option, value) => option.code === value.code, renderInput: params => (_jsx(TextField, { ...params, placeholder: intl.formatMessage({ id: 'Search currencies...' }), error: !!errors, InputProps: {
                        ...params.InputProps,
                        startAdornment: (_jsxs(_Fragment, { children: [_jsx(InputAdornment, { position: 'start', children: _jsx(SearchIcon, { size: 20 }) }), params.InputProps.startAdornment] })),
                    } })) }), _jsx(ErrorList, { show: !!errors, errors: errors })] }));
};
